import { Box, CircularProgress, Typography } from "@mui/material";
import { styled } from "@mui/material/styles";
import { BORDER_RADIUS, COLORS } from "app/theme";
import { useMemo } from "react";
import {
  Area,
  AreaChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import type { MilkQualityTrendData } from "../hooks/useInsightsDashboardQueries";

const Card = styled(Box)({
  backgroundColor: "white",
  borderRadius: BORDER_RADIUS.medium,
  boxShadow: "0 2px 8px rgba(0, 0, 0, 0.08)",
  padding: 20,
});

type ChartRow = { period: string; volume: number | null };

function formatDate(dateStr: string): string {
  const d = new Date(dateStr);
  return d.toLocaleDateString("en-GB", { month: "short", year: "2-digit" });
}

function formatLitres(v: number): string {
  if (v >= 1_000_000)
    return `${(v / 1_000_000).toLocaleString(undefined, {
      maximumFractionDigits: 1,
    })}M`;
  if (v >= 1000)
    return `${(v / 1000).toLocaleString(undefined, {
      maximumFractionDigits: 0,
    })}k`;
  return v.toLocaleString(undefined, { maximumFractionDigits: 0 });
}

interface Props {
  data: MilkQualityTrendData[] | undefined;
  isLoading: boolean;
}

export default function MilkVolumeTrendChart({ data, isLoading }: Props) {
  const chartData = useMemo<ChartRow[]>(() => {
    if (!data) return [];
    return data
      .map((r) => {
        // Total = average volume × number of records (avg = sum / count).
        const avg = r["MilkQuality.avgVolumeLiters"];
        const count = r["MilkQuality.count"];
        const volume =
          avg != null && count != null ? Number(avg) * Number(count) : null;
        return {
          period: String(r["MilkQuality.periodStart"]).split("T")[0],
          volume,
        };
      })
      .sort((a, b) => a.period.localeCompare(b.period));
  }, [data]);

  if (isLoading) {
    return (
      <Card sx={{ display: "flex", justifyContent: "center", py: 4 }}>
        <CircularProgress size={24} />
      </Card>
    );
  }

  if (chartData.length === 0) {
    return (
      <Card>
        <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
          Total Milk Volume
        </Typography>
        <Typography variant="body2" color="text.secondary">
          No data available
        </Typography>
      </Card>
    );
  }

  return (
    <Card>
      <Typography
        variant="subtitle2"
        sx={{ fontWeight: 600, color: "#18181b", mb: 0.5 }}
      >
        Total Milk Volume
      </Typography>
      <Typography
        variant="caption"
        sx={{ color: "#71717a", mb: 1.5, display: "block" }}
      >
        Total volume collected across the programme over time
      </Typography>
      <Box sx={{ width: "100%", height: 240 }}>
        <ResponsiveContainer>
          <AreaChart data={chartData} margin={{ top: 5, right: 8, bottom: 0 }}>
            <defs>
              <linearGradient id="volumeFill" x1="0" y1="0" x2="0" y2="1">
                <stop
                  offset="0%"
                  stopColor={COLORS.daisy[500]}
                  stopOpacity={0.35}
                />
                <stop
                  offset="100%"
                  stopColor={COLORS.daisy[500]}
                  stopOpacity={0.02}
                />
              </linearGradient>
            </defs>
            <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
            <XAxis
              dataKey="period"
              tickFormatter={formatDate}
              tick={{ fontSize: 11, fill: "#71717a" }}
              tickLine={false}
              interval="preserveStartEnd"
              minTickGap={36}
            />
            <YAxis
              tick={{ fontSize: 11, fill: "#71717a" }}
              tickLine={false}
              axisLine={false}
              domain={[0, "auto"]}
              width={40}
              tickFormatter={formatLitres}
            />
            <Tooltip
              labelFormatter={(label) => {
                const d = new Date(String(label));
                return d.toLocaleDateString("en-GB", {
                  day: "numeric",
                  month: "short",
                  year: "numeric",
                });
              }}
              formatter={(value: number) => [
                value.toLocaleString(undefined, { maximumFractionDigits: 0 }) +
                  " L",
                "Total Volume",
              ]}
            />
            <Area
              type="monotone"
              dataKey="volume"
              stroke={COLORS.daisy[700]}
              strokeWidth={2.5}
              fill="url(#volumeFill)"
              connectNulls
              dot={false}
            />
          </AreaChart>
        </ResponsiveContainer>
      </Box>
    </Card>
  );
}
